import { IWishlistCardSwitch } from '@models/IWishlistCardSwitch';
import CardTypeEnum from '@models/enums/CardTypeEnum';
import ColourSwatchVariantEnum from '@models/enums/ColourSwatchVariantEnum';
import ProductCardVariantEnum from '@models/enums/ProductCardVariantEnum';
import ColourSwatchCard, {
  ColourSwatchCardBG,
} from '@molecules/ColourSwatchCard/ColourSwatchCard';
import HotspotCard, {
  HotspotCardVariant,
} from '@molecules/HotspotCard/HotspotCard';
import ProductCard from '@molecules/ProductCard/ProductCard';
import { useLikedItemsStore } from '@state/WishlistStore';
import { mapWishlistData } from '@utility/helpers/mapWishlistData';
import { useCallback, useEffect, useState } from 'react';

interface Props extends IWishlistCardSwitch {
  readonly isListing?: boolean;
  readonly refreshImageUrlEndpoint?: string;
  readonly showLikeButton?: boolean;
  readonly wishlistId?: number;
}

const refreshImageUrl = async (imageUrl: string, endpoint: string) => {
  try {
    const response = await fetch(`${endpoint}?url=${imageUrl}`);
    if (response.ok) {
      imageUrl = await response.text();
    }
  } catch (error) {
    console.error('Failed to refresh image url:', error);
  }
  return imageUrl;
};

const WishlistCardSwitch = ({
  isListing,
  item,
  refreshImageUrlEndpoint,
  showLikeButton = true,
  wishlistId,
}: Props) => {
  const { updateLikedItem } = useLikedItemsStore();
  const [data, setData] = useState(mapWishlistData(item.entity));
  const { collection, newModel, data: entityData, fields } = item.entity;

  const updateItemImageUrl = useCallback(
    async (newUrl: string) => {
      try {
        if (newModel) {
          item.entity.data = { ...data };
        } else if (entityData) {
          entityData.desktopImage = newUrl;
          entityData.mobileImage = newUrl;
        } else if (fields) {
          const desktopImageField = fields.image?.value?.fields?.desktop_image;
          if (desktopImageField?.value) {
            desktopImageField.value = newUrl;
          }
        }

        await updateLikedItem(item);
      } catch (error) {
        console.error('Error updating image url:', error);
      }
    },
    [item],
  );

  useEffect(() => {
    if (!refreshImageUrlEndpoint) return;

    const updateImageUrl = (image: any) => {
      if (image.url.includes('.blob.')) {
        refreshImageUrl(image.url, refreshImageUrlEndpoint).then((newImage) => {
          image.url = newImage;
          setData({ ...data });
          updateItemImageUrl(newImage);
        });
      }
    };

    const updateVideoSource = (video: any) => {
      if (video.videoSource.includes('.blob.')) {
        refreshImageUrl(video.videoSource, refreshImageUrlEndpoint).then(
          (newSource) => {
            video.videoSource = newSource;
            setData({ ...data });
            updateItemImageUrl(newSource);
          },
        );
      }
    };

    if (collection === CardTypeEnum.Products) {
      updateImageUrl(data.productImage.mobile);
    } else if (collection === CardTypeEnum.Inspirations && data.image) {
      updateImageUrl(data.image);
    } else if (collection === CardTypeEnum.Inspirations && data.video) {
      updateVideoSource(data.video);
    }
  }, [collection, refreshImageUrlEndpoint]);

  switch (collection) {
    case CardTypeEnum.Products:
      return (
        <ProductCard
          showLikeButton={showLikeButton}
          wishlistId={wishlistId}
          {...data}
          detailedView={false}
          variant={
            isListing
              ? ProductCardVariantEnum.TallOnDesktopOnly
              : ProductCardVariantEnum.Tall
          }
        />
      );
    case CardTypeEnum.Colours:
      return (
        <ColourSwatchCard
          showLikeButton={showLikeButton}
          wishlistId={wishlistId}
          {...data}
          variant={ColourSwatchVariantEnum.Default}
          bgColour={ColourSwatchCardBG.White}
        />
      );
    case CardTypeEnum.Inspirations:
      return (
        <HotspotCard
          aspectRatio={2 / 3}
          showLikeButton={showLikeButton}
          wishlistId={wishlistId}
          {...data}
          variant={HotspotCardVariant.tallWithTab}
        />
      );
    default:
      return null;
  }
};

export default WishlistCardSwitch;
